import {Dictionary} from "../interfaces/commonInterfaces";
import {Hash} from "~/interfaces/hashInterfaces";

export interface IPagination {
  per_page: number;
  page: number;
  last_page: number;
  pages: string[];
  sort: string | string[] | number;
  direction: string | string[] | 'asc' | 'desc';
  filter: object;
}

export default class Pagination implements IPagination {
  per_page: number;
  page: number;
  last_page: number;
  pages: string[];
  sort: string | number; // sometimes an enum
  direction: 'asc' | 'desc' = 'asc';
  filter: object

  constructor(
      { per_page = 10,
        page = 1,
        last_page = 1,
        pages = ['1'],
        sort = null,
        direction = 'asc',
        filter = {},
      }: Partial<IPagination> = {
        per_page: 10,
        page: 1,
        last_page: 1,
        pages: ['1'],
        sort: null,
        direction: 'asc',
        filter: {},
      }) {
    this.per_page = per_page;
    this.page = page;
    this.last_page = last_page;
    this.pages = pages;
    this.setSort(sort)
    this.setDirection(direction)
    this.filter = filter || {}
  }


  asJson(options: Hash = null) {
    const json = {
      page: this.page,
      sort: this.sort,
      direction: this.direction
    };
    // options hash allows custom param keys for values
    if (options && Object.keys(options)) {
      Object.keys(options).forEach(k => {
        if (this.hasOwnProperty(k)) {
          json[options[k]] = this[k];
        }
      });
    }
    return json
  }

  sortAsJson() {
    return {
      sort: this.sort,
      direction: this.direction
    };
  }

  setSortAndDirection(newSort: IPagination['sort'], newDirection: IPagination["direction"] = null): void {
    this.setDirection(newDirection, newSort);
    this.setSort(newSort);
  }

  setDirection(newDirection: IPagination["direction"], newSort?: IPagination['sort']): void {
    let dir: string = Array.isArray(newDirection) ? newDirection[0] : newDirection // do not support direction as an array
    if (dir) {
      this.direction = (dir === 'asc' || dir === 'desc') ? dir : 'asc'
    } else if (this.sort && this.sort === newSort) {
      this.direction = this.direction === "asc" ? "desc" : "asc";
    } else {
      this.direction = "asc";
    }
  }

  updateFrom(json) {
    // updates values from the backend Pagination.rb#as_json
    Object.keys(json).forEach(k => {
      this[k] = json[k]
    });
  }

  setSort(newSort: IPagination['sort'] = ''): void {
    this.sort = Array.isArray(newSort) ? newSort[0] : newSort // do not support sort as an array
  }

  get prevPage(): number {
    return this.page > 1 ? this.page - 1 : null;
  }

  get nextPage(): number | null {
    return this.page < this.last_page ? this.page + 1 : null;
  }

  turnPage(num: number): number {
    // will do nothing if cannot turn page
    // not sure if that is the best behavior for general use

    let tmpPage = this.page + num;
    if (!(tmpPage > this.last_page || tmpPage < 1)) {
      this.page += num;
    }
    return this.page;
  }

  get hasReachedEnd(): boolean {
    return !this.shouldPaginate;
  }

  get shouldPaginate(): boolean {
    return this.page < this.last_page;
  }

  static stripParams(object: object) {
    ['page', 'per_page', 'sort', 'direction'].forEach(param => {
      delete object[param];
    });
    return object;
  }

  static setDefaultPaging(object: Dictionary<string | string[]>) {
    object.page = '1';
    return object;
  }
}